import { RiArrowDownSLine, RiArrowRightSLine } from "@remixicon/react";
import type { WrappedCoin } from "@screener/protocol";
import { TableCell, TableRow } from "@screener/uikit/components/table";
import { cn } from "@screener/uikit/lib/utils";
import type { KeyboardEvent as ReactKeyboardEvent } from "react";
import {
  formatUsd,
  normalizedPairLabel,
  pluralForm,
  sideLabel,
  situationKindLabel,
  tickerLabel,
  venueOf,
  wrappedMark,
} from "@/lib/labels";
import { instrumentKey } from "./cards";
import type { RowKeyLike } from "./density-row";
import { volumeBarFraction } from "./derive";
import { Elapsed } from "./elapsed";
import { formatDistance, formatPrice } from "./format";
import { normalizePrice } from "./pin-match";
import { SideBadge } from "./side-badge";
import type { SituationRow as SituationRowData } from "./situation-derive";
import { VenueMark } from "./venue-mark";
import { WrappedMark } from "./wrapped-mark";

export type SituationRowKeyAction = "toggle" | null;

/**
 * Решает, какое действие несёт нажатая клавиша на строке группы §5: Enter и
 * пробел раскрывают/сворачивают, всё остальное (в первую очередь стрелки)
 * возвращает `null` и обязано дойти до навигации списка — тот же приём, что
 * `resolveRowKeyAction` у строки плотности (design.md, решение 3).
 */
export function resolveSituationRowKeyAction(event: RowKeyLike): SituationRowKeyAction {
  if (event.key === "Enter" || event.key === " ") {
    return "toggle";
  }
  return null;
}

/**
 * Цена ситуации, выведенная клиентом из якорей видимых участников (§5,
 * design.md решение 2): одно число при совпадении якорей, диапазон от
 * наименьшего к наибольшему при расхождении, и прочерк, когда конечного
 * якоря нет ни у одного видимого участника — «0» уверенно соврал бы там,
 * где верной цены не существует.
 */
function priceLabel(row: SituationRowData): string {
  if (row.anchorMin === null || row.anchorMax === null) {
    return "—";
  }
  return normalizePrice(row.anchorMin) === normalizePrice(row.anchorMax)
    ? formatPrice(row.anchorMin)
    : `${formatPrice(row.anchorMin)}–${formatPrice(row.anchorMax)}`;
}

/**
 * Как строка группы называет инструмент (§63). Ключ группы рынка не несёт, а
 * при группировке по паре и биржи, поэтому «один ли инструмент» решается по
 * всем участникам ситуации из контракта, а не по видимым после фильтров:
 * отсеянный участник с другого рынка не делает группу одним инструментом.
 * Один инструмент (биржа, рынок, пара) — его тикер с базой биржи из индекса
 * (нет записи — приведённая база); иначе приведённая пара.
 */
export function groupInstrumentLabel(
  row: SituationRowData,
  exchangeBases: ReadonlyMap<string, string>,
): string {
  const [first, ...rest] = row.situation.members;
  if (
    first !== undefined &&
    rest.every((member) => instrumentKey(member.instrument) === instrumentKey(first.instrument))
  ) {
    return tickerLabel(first.instrument, exchangeBases.get(instrumentKey(first.instrument)) ?? "");
  }
  return normalizedPairLabel(row.situation.key);
}

/**
 * Сколько рынков группы стоит сверх ведущей плотности — число для «+N»
 * (1.51, Д2). Берётся из агрегата контракта, а не из видимых участников:
 * агрегат отбором не правится (`web-situation-list`).
 */
function otherMarketsCount(row: SituationRowData): number {
  return Math.max(row.situation.aggregate.markets - 1, 0);
}

/** Подпись «+N» для чтения с экрана: «и ещё 2 рынка». */
function otherMarketsLabel(count: number): string {
  return `и ещё ${count} ${pluralForm(count, "рынок", "рынка", "рынков")}`;
}

/**
 * Имя строки группы для читающего с экрана.
 *
 * `role="button"` на `<tr>` отменяет табличную семантику, и без своего имени
 * кнопка называлась бы склейкой всех тринадцати ячеек — вместе с прочерками
 * и пустой ячейкой действий. Названо то же и в том же порядке, что читает
 * глазом видящий: пара, сторона, тип ситуации §5, сколько участников.
 */
function groupRowLabel(
  row: SituationRowData,
  wrapped: readonly WrappedCoin[],
  exchangeBases: ReadonlyMap<string, string>,
): string {
  const { situation } = row;
  const members = row.knownCount > 0 ? row.knownCount : situation.members.length;
  const counted = `${members} ${pluralForm(members, "плотность", "плотности", "плотностей")}`;
  const others = otherMarketsCount(row);
  return [
    groupInstrumentLabel(row, exchangeBases),
    wrappedMark(situation.key.base, wrapped),
    sideLabel(situation.key.side),
    situationKindLabel(situation.kind),
    counted,
    venueOf(row.leading),
    others > 0 ? otherMarketsLabel(others) : undefined,
    priceLabel(row),
  ]
    .filter((part) => part !== undefined)
    .join(", ");
}

interface SituationRowProps {
  row: SituationRowData;
  /** Перечень обёрнутых монет из приветствия. */
  wrapped: readonly WrappedCoin[];
  /** Индекс баз в написании биржи (`StreamState.exchange_bases`). */
  exchangeBases: ReadonlyMap<string, string>;
  /** Раскрыта ли группа — состояние живёт в экране (design.md, решение 4). */
  expanded: boolean;
  /** Единственная точка табуляции списка сейчас — та же схема, что у `DensityRow`. */
  focusable: boolean;
  onFocus: () => void;
  onToggle: () => void;
  onKeyDown: (event: ReactKeyboardEvent<HTMLTableRowElement>) => void;
}

/**
 * Свёрнутая строка ситуации §5. Всё, что в ней стоит, — ключ и агрегат
 * контракта: клиент не пересчитывает ничего, кроме диапазона якорей, которого
 * в контракте нет вовсе (design.md, решение 2).
 *
 * Рисуется теми же колонками, что и строка плотности, а не одной ячейкой во
 * всю ширину: во всю ширину строки группы не задавали ширин колонкам вовсе,
 * и пока не раскрыта ни одна группа, шапка раскладывалась сама по себе —
 * а первое же раскрытие двигало её (найдено первым боевым подъёмом,
 * 22.09.2026). Величина, которой у группы нет, называется прочерком: это
 * честнее пустой ячейки, читающейся как ноль.
 */
export function SituationRow({
  row,
  wrapped,
  exchangeBases,
  expanded,
  focusable,
  onFocus,
  onToggle,
  onKeyDown,
}: SituationRowProps) {
  const { situation } = row;
  const otherMarkets = otherMarketsCount(row);
  // Сторона названа жёлтым, если у видимых участников есть флаги ММ — тем же
  // правилом, что и у строки плотности (§9), только по всей группе разом.
  const flagged = row.members.some((member) => member.mm_flags.length > 0);
  const barPercent = volumeBarFraction(situation.aggregate.total_usd) * 100;

  return (
    <TableRow
      data-side={situation.key.side}
      data-row-key={row.key}
      tabIndex={focusable ? 0 : -1}
      role="button"
      aria-expanded={expanded}
      // `role="button"` отменяет табличную семантику строки, и без своего
      // имени скринридер склеил бы его из всех тринадцати ячеек — «2 рынка
      // BID BTC/USDT … — 1м–5м Stacked 3». Имя называет то же, что читает
      // глазом видящий: пару, сторону, тип ситуации и число участников.
      aria-label={groupRowLabel(row, wrapped, exchangeBases)}
      onFocus={onFocus}
      onClick={onToggle}
      onKeyDown={(event) => {
        const action = resolveSituationRowKeyAction(event);
        if (action === "toggle") {
          event.preventDefault();
          onToggle();
          return;
        }
        onKeyDown(event);
      }}
      className={cn(
        "h-7 cursor-pointer bg-muted/30 font-[450] tabular-nums",
        "focus-visible:bg-accent focus-visible:text-accent-foreground focus-visible:outline-none",
      )}
    >
      {/* Полоска стороны — та же, что у строки плотности: колонки обеих
          строк обязаны совпадать до единой, иначе шапка не описывает ни ту,
          ни другую. */}
      <TableCell className="w-1.5 p-0">
        <span
          aria-hidden="true"
          className={cn("block h-7 w-1.5", situation.key.side === "bid" ? "bg-bid" : "bg-ask")}
        />
      </TableCell>

      {/* Логотип и рынок ведущей плотности — той, чья дистанция стоит в
          строке (1.51, §64 п. 1); в режиме `by_exchange` это и есть биржа
          ключа. Для межрыночной группы рядом — «+N» остальных рынков по
          агрегату контракта, а не по видимым участникам. */}
      <TableCell className="px-2 text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <VenueMark instrument={row.leading} />
          {otherMarkets > 0 ? (
            <span aria-hidden="true" className="text-[11px]">
              +{otherMarkets}
            </span>
          ) : null}
        </span>
      </TableCell>

      <TableCell className="px-2 font-medium text-foreground">
        <span className="inline-flex items-center gap-1">
          {expanded ? (
            <RiArrowDownSLine
              aria-hidden="true"
              className="size-3.5 shrink-0 text-muted-foreground"
            />
          ) : (
            <RiArrowRightSLine
              aria-hidden="true"
              className="size-3.5 shrink-0 text-muted-foreground"
            />
          )}
          {groupInstrumentLabel(row, exchangeBases)}{" "}
          <WrappedMark base={situation.key.base} wrapped={wrapped} className="ml-1.5" />
        </span>
      </TableCell>

      <TableCell className="px-2">
        <SideBadge side={situation.key.side} flagged={flagged} />
      </TableCell>

      <TableCell className="px-2 text-right font-medium">{priceLabel(row)}</TableCell>

      {/* Дистанция минимальна среди видимых участников; рынок, которому это
          число принадлежит, называет колонка «Биржа · рынок» (ведущая
          плотность, 1.51), а не текст в этой ячейке. */}
      <TableCell className="px-2 text-right">
        {formatDistance(row.nearest?.distancePct ?? null)}
      </TableCell>

      <TableCell className="relative px-2 text-right">
        {/* Полоса по постоянной логарифмической шкале §9 (1.9) — та же, что и
            у строки плотности: одна и та же величина обязана выглядеть
            одинаково в обоих режимах. */}
        <span
          aria-hidden="true"
          className={cn(
            "absolute inset-y-[3px] right-0",
            situation.key.side === "bid" ? "bg-bid-wash" : "bg-ask-wash",
          )}
          style={{ width: `${barPercent}%` }}
        />
        <span className="relative font-medium">{formatUsd(situation.aggregate.total_usd)}</span>
      </TableCell>

      {/* Уровней у группы нет: они свойство одной плотности, и складывать их
          по группе контракт не даёт. Прочерк, а не пустая ячейка — пустая
          читалась бы нулём. */}
      <TableCell className="px-2 text-right text-muted-foreground">—</TableCell>

      <TableCell className="px-2 text-right">
        {/* Момент самой молодой плотности даёт минимальное время жизни группы,
            самой старой — максимальное (§5, `SituationAggregateView`). */}
        <Elapsed sinceMs={situation.aggregate.first_seen_max_ms} />–
        <Elapsed sinceMs={situation.aggregate.first_seen_min_ms} />
      </TableCell>

      {/* Состояния FSM у группы нет, а тип ситуации §5 есть — и он обязан
          быть виден словом, а не только цветом: завал отличим от одной
          стены, увиденной несколькими рынками. */}
      <TableCell className="px-2 text-[11px] text-muted-foreground">
        {situationKindLabel(situation.kind)}
      </TableCell>

      <TableCell className="px-2 text-right">{situation.aggregate.approaches_count}</TableCell>

      {/* Скрытое отбором стоит здесь, а не рядом с агрегатом: агрегат по всей
          группе рядом с неполным составом иначе читался бы как сумма
          видимого (web-situation-list). */}
      <TableCell className="px-2 text-[11px] text-muted-foreground">
        {row.hiddenCount > 0 ? `скрыто отбором: ${row.hiddenCount} из ${row.knownCount}` : null}
      </TableCell>

      {/* Действий у строки группы нет — они у участников. */}
      <TableCell className="w-[132px] p-0" />
    </TableRow>
  );
}
